/**
 * GovEarner Configuration (SPARC RFP platform)
 * Global opportunity discovery and proposal automation for GovEarner
 * 
 * This configuration extends TraceKit core with ALL features enabled:
 * - Requirement extraction from RFP documents
 * - Past proposal search and evidence retrieval
 * - Draft generation with evidence citations
 * - Word count optimization
 * - Professional review workflow
 * - Compliance monitoring
 * - International + Canadian opportunity sources
 */

import { baseConfig } from './base.config';
import { TraceKitConfig } from '../packages/core/src/types/config';
import { sparcThemeConfig } from '../packages/apps/sparc-rfp/src/config/theme';

// SPARC RFP Agent specific IDs (from tenant setup)
// These defaults can be overridden via environment variables in main.tsx
// Set VITE_TENANT_ID and VITE_PROJECT_ID in launch.json
export const SPARC_TENANT_ID = '52fb10b9-043c-43a9-bbbb-a7d56258a192'; // SPARC RFP tenant
export const SPARC_PROJECT_ID = 'ce12c2b6-a56d-4a4c-abd2-56adf836fba2'; // SPARC RFP production project

export const sparcConfig: TraceKitConfig = {
  ...baseConfig,
  
  branding: {
    appName: 'GovEarner',
    logo: '/sparc-fulllogo.png',
    logoUrl: '/sparc-icon.png',
    logoAlt: 'GovEarner Logo',
    favicon: '/sparc-favicon.ico',
    primaryColor: '#0070b0', // SPARC blue (sampled from official "Igniting Innovations" logo wordmark)
    secondaryColor: '#005a8d', // SPARC dark blue (derived shade of sampled logo blue)
    companyName: 'SPARC Global',
    platformTitle: 'GovEarner',
    platformSubtitle: 'Global Opportunity Discovery & Proposal Automation',
    poweredBy: 'SPARC',
    feedbackProductName: 'SPARC RFP',
  },
  
  theme: sparcThemeConfig,
  
  features: {
    ...baseConfig.features,
    rag: true,              // Required for requirement extraction and proposal search
    reports: true,          // Core feature - RFPs are reports (report_type='rfp_proposal')
    entities: true,          // ENABLED - Policy Pack has 20+ entity types
    sources: true,           // Required for RFP document upload and management
    jobs: true,             // Required for document ingestion and report generation
    webhooks: false,        // Not using webhooks for RFP workflow
    cache: true,            // Enable caching for performance (query results, evidence)
    admin: false,           // Admin features not needed for RFP workflow
  },
  
  deployment: {
    environment: 'production',
    domain: 'govearner.com',
    enableAnalytics: true,
  },
  
  customization: {
    defaultTenantId: SPARC_TENANT_ID,
    defaultProjectId: SPARC_PROJECT_ID,
    hideTenantSelector: true,
    showWorkspaceSelectorsPermission: 'users.manage',
    settingsTechnicalTabsPermission: 'data_management.read',

    // Feature flags - ALL FEATURES ENABLED (unlike Keystone's limited set)
    featureFlags: {
      // UI/UX Features - ALL ENABLED
      enableCalendarView: true,              // Calendar view in opportunities dashboard
      enableCustomSections: true,             // Custom sections tab in reports
      enableWordCountOptimizer: true,        // Word count optimizer
      enableQualityAssurance: true,          // Quality assurance tab in reports
      enableReviewWorkflow: true,             // Review workflow tab in reports
      enableAvailabilityCalendar: true,       // Availability calendar tab in staff management
      enableQualificationsCerts: true,        // Qualifications & certifications tab
      enableExecutiveDashboard: false,        // DISABLED - not visible in left menu for SPARC
      enableWorkPlanExecution: false,         // Work Plan tab - DISABLED for SPARC RFP
      enableProposalSearch: false,            // Hide "Past Proposal Context" tab in Proposal Management (SPARC)
      /** Allow Data Pipeline for all logged-in users (nav uses data_management.read by default; without this, /data redirects home). */
      dataPipelineOpenAccess: true,
      
      // Report viewer tabs - ALL ENABLED
      showCommentsAndReviewTab: true,         // Comments & Review tab
      showAnalyticsTab: true,                 // Analytics tab in proposal report view
      showActionsTab: true,                   // Actions tab
      showSharingTab: true,                   // Sharing tab in report viewer
      
      // Staff Management - DISABLED for SPARC (not in left menu)
      showStaffManagementStandalone: false,   // DISABLED - not visible in left menu for SPARC
      
      // Government Data - Show zone buttons for international
      showGovernmentDataZoneButtons: true,   // Zone filter buttons for international opportunities
      enableGovernmentDataGeographicZones: true,
      
      // Template Generation Methods
      enableProductionTemplate: true,         // AI-Derived (Production) template generation
      enableLegacyTemplate: true,             // Legacy AI-Derived template generation

      // Home page mode - show workspace/project selector at '/' instead of HomeDashboard
      workspaceHomePage: true,

      // SPARC-012: TEC Queue + weekly batch review (admin-only)
      enableTecWorkflow: true,

      // WS3: costing-platform integration (SPARC tenant only)
      enableCostingPlatform: true,

      // Recommended Tenders (URL tab=weekly-review) is a SPARC ALL BD surface
      enableWeeklyReview: true,

      // Chip stays on SPARC; default OFF so civil (30% or below) is listed and sorted last
      defaultSoftwareDefinedFilter: false,
    },
    
    // SPARC-specific labels
    opportunitiesDashboardLabel: 'Global Opportunities',
    integrationsTechRoles: ['admin_tech'],
    workPlanVisibleToPermission: 'users.manage',
    workPlanExecutionDevEmails: ['parsa@alpyne.ai'],
    weeklyReviewDevEmails: [
      'arnav@sparcglobal.ca',
      'parsa@alpyne.ai',
      'rupkiran@sparcglobal.ca',
      'eric@sparcglobal.ca',
      'sg.ops@sparcindia.com',
    ],
    // Opportunity email send, tab-complete, and header feedback. Keystone is not on this list.
    allowedEmailDomains: ['sparcglobal.ca', 'sparcindia.com', 'alpyne.ai'],
    feedbackRecipients: [
      'parsa@sparcglobal.ca',
      'krish@sparcglobal.ca',
    ],
    supportEmail: 'parsa@sparcglobal.ca',
    // SPARC ALL-access landing zone. World stays on the toggle; stored choice wins after first pick.
    defaultGeographicZone: 'canada',
    
    // SPARC-specific search sample queries for UI suggestions
    searchSampleQueries: [
      // Requirement Extraction Queries
      'Extract all mandatory requirements from this RFP with section and page numbers',
      'List all technical requirements with their specifications and evaluation criteria',
      'Identify all submission requirements including format, deadline, and word limits',
      'Extract all administrative requirements (forms, certifications, etc.)',
      'List all commercial requirements (pricing, payment terms, etc.)',
      'Identify all legal requirements (liability, insurance, etc.)',
      'Extract evaluation criteria and scoring methodology',
      'List all deliverables and their specifications',
      
      // Past Proposal Search Queries (SPARC focus areas)
      'Find past proposals for mining infrastructure projects',
      'Search for similar international development proposals',
      'Show past performance examples for forestry management projects',
      'Find winning proposals for infrastructure development',
      'Search for proposals similar to this RFP scope',
      'Show past proposals with similar technical requirements',
      'Find proposals in the Infrastructure Development service line',
      'Search for proposals with similar project value range',
      'Find past proposals for agricultural development',
      'Show past performance for technology innovation projects',
      
      // Draft Generation Queries
      'Generate executive summary highlighting SPARC\'s expertise for [project type]',
      'Create technical approach section based on past performance',
      'Write past performance section with specific project examples similar to this RFP',
      'Generate project team section with relevant qualifications',
      'Create schedule and deliverables section based on RFP requirements',
      'Write company overview emphasizing capabilities relevant to this project',
      
      // Evidence Retrieval Queries
      'Find evidence supporting [specific claim]',
      'Retrieve past performance examples for [project type]',
      'Search for similar projects completed by SPARC',
      'Find evidence of [specific qualification or certification]',
      
      // International Development Queries
      'What are the World Bank procurement requirements?',
      'Show me compliance documents for international development projects',
      'Generate RFP response for infrastructure development',
      'Find opportunities in developing countries',
      'What documents do we need for international bids?',
      'Summarize requirements for mining project proposals',
    ],
    
    // RFP-specific entity types (same as Keystone - plugin expects these)
    entityTypes: [
      // Government Opportunities (3)
      'govt_opportunity',
      'govt_grant',
      'govt_contract_award',
      
      // Internal Documents (5)
      'proposal_document',
      'workplan_document',
      'rfp_document',
      'bid_document',
      'submission_document',
      
      // Company Intelligence (5)
      'staff_member',
      'past_project',
      'client_record',
      'capability_statement',
      'sop_reference',
      
      // Opportunity Analysis (3)
      'opportunity_requirement',
      'opportunity_match_score',
      'rfp_requirement',
      
      // Evidence & Compliance (3)
      'technical_approach',
      'past_performance_example',
      'qualification_certification',
      
      // Project Execution (3)
      'project_team_composition',
      'budget_estimate',
      'subcontractor',
    ],
    
    // Custom routes for RFP-specific pages (will be registered by plugin)
    customRoutes: [],
  },
  
  plugins: [
    // Plugin will be added in main.tsx:
    // import { rfpAgentPlugin } from '../../plugins/rfp-agent/src';
    // sparcConfig.plugins = [sparcRfpPlugin];
  ],
};

/**
 * RFP-Specific Configuration Constants
 * SPARC-specific values for RFP agent functionality
 */
export const RFP_CONFIG = {
  // Policy Pack Configuration
  policyPack: {
    id: 'sparc_rfp_agent_v1',
    version: '1.0.0',
    tenantId: SPARC_TENANT_ID,
  },
  
  // Evidence Requirements
  evidence: {
    minConfidence: 0.7,
    minItemsPerSection: 3,
    preferredWinStatus: 'Won',
    recentThresholdDays: 730, // 2 years
    similarityThreshold: 0.6,
  },
  
  // Word Count Optimization
  wordOptimization: {
    enabled: true,
    preserveCitations: true,
    preserveEvidence: true,
    targetReductionPercentage: 0.15, // 15% reduction
    maxIterations: 3,
  },
  
  // Professional Review
  professionalReview: {
    required: true,
    reviewerRoles: ['Senior Consultant', 'Technical Lead', 'Project Manager'],
    requiredBeforeStatus: 'submitted',
    auditTrailRequired: true,
  },
  
  // DOCX Export Configuration
  docxExport: {
    templatePath: 'configs/sparc-assets/Proposal_Template.docx',
    storageType: 'azure_blob' as 'filesystem' | 'blob',  // Cloud storage for SPARC
    
    formatting: {
      bodyText: {
        fontName: 'Calibri',
        fontSizePt: 11,
        spacingAfterPt: 6,
        spacingBeforePt: 0,
        lineSpacing: 1.15,
      },
      headings: {
        h1: {
          fontSizePt: 16,
          spacingBeforePt: 18,
          spacingAfterPt: 6,
        },
        h2: {
          fontSizePt: 14,
          spacingBeforePt: 12,
          spacingAfterPt: 6,
        },
        h3: {
          fontSizePt: 12,
          spacingBeforePt: 10,
          spacingAfterPt: 4,
        },
        h4: {
          fontSizePt: 11,
          spacingBeforePt: 8,
          spacingAfterPt: 4,
        },
      },
      lists: {
        spacingBeforePt: 0,
        spacingAfterPt: 0,
        spacingAfterLastPt: 6,
      },
      tables: {
        spacingBeforePt: 12,
        spacingAfterPt: 6,
      },
      codeBlocks: {
        spacingBeforePt: 6,
        spacingAfterPt: 6,
      },
      blockquotes: {
        spacingBeforePt: 6,
        spacingAfterPt: 6,
      },
    },
    
    exportGuardrails: {
      requireApproval: ['docx', 'pdf', 'v2'],
      allowedStatuses: ['approved', 'archived', 'submitted'],
    },
  },
  
  // Citation Format
  citations: {
    style: 'inline',
    format: '[{source}, p.{page}]',
    requirePageNumbers: true,
    requireSourceLinks: true,
    minCitationsPerSection: 2,
  },
  
  // Data Sources
  dataSources: {
    historicalRFPs: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 100,
    },
    historicalProposals: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 200,
    },
    sparcSOPs: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 150,
    },
  },
  
  // Report Types
  reportTypes: {
    rfpProposal: {
      id: 'rfp_proposal',
      defaultStatus: 'draft',
      workflowStages: ['draft', 'in_review', 'approved', 'submitted', 'archived'],
      requiredSections: [
        'Executive Summary',
        'Company Overview',
        'Technical Approach',
        'Past Performance',
        'Project Team',
        'Schedule and Deliverables',
      ],
      optionalSections: [
        'Budget',
        'Risk Management',
        'Quality Assurance',
      ],
    },
  },
  
  // Service Lines (SPARC Global focus areas)
  serviceLines: [
    'Mining & Resources',
    'Environmental Consulting',
    'Infrastructure Development',
    'Technology & Innovation',
    'Agriculture & Agribusiness',
    'Forestry & Natural Resources',
    'Urban Development',
    'International Development',
  ],
  
  // Regulatory Frameworks
  regulatoryFrameworks: [
    'World Bank Procurement',
    'UN Procurement',
    'Canadian Federal Procurement',
    'US Federal Acquisition Regulation (FAR)',
    'International Development Standards',
  ],
} as const;
